import { Camera, CircleAlert, CircleCheck, Clock, FileCheck2 } from "lucide-react";
import { useMemo } from "react";
import type { CSSProperties, ReactNode } from "react";
import { calculateAttendanceCount } from "../shared/attendance";
import { buildHealthCertificateSummary } from "../shared/health";
import { buildReportStatuses } from "../shared/reports";
import type { HealthStatus, PublicDisplaySnapshot, QualityReportStatus } from "../shared/types";
import { useCanvasScale } from "./useCanvasScale";

interface DisplayScreenProps {
  snapshot: PublicDisplaySnapshot;
  now: Date;
}

const HEALTH_LABELS: Record<HealthStatus, string> = {
  normal: "正常",
  expiring: "临期",
  expired: "过期",
  missing: "缺失"
};

const HEALTH_CLASS: Record<HealthStatus, string> = {
  normal: "status-normal",
  expiring: "status-expiring",
  expired: "status-expired",
  missing: "status-missing"
};

const RETENTION_VISIBLE_ROWS = 5;
const HEALTH_VISIBLE_ROWS = 5;

const RETENTION_COLUMNS = ["序号", "菜品", "克重", "餐次", "留样人"];
const HEALTH_COLUMNS = ["姓名", "健康证编号", "状态", "有效期至"];

export function DisplayScreen({ snapshot, now }: DisplayScreenProps) {
  const { ref, scale } = useCanvasScale();
  const reports = useMemo(() => buildReportStatuses(snapshot.reportUploads, now), [snapshot.reportUploads, now]);
  const healthCertificates = useMemo(() => buildHealthCertificateSummary(snapshot.healthCertificates.rows, now), [snapshot.healthCertificates.rows, now]);
  const attendanceCount = calculateAttendanceCount(snapshot.attendanceRule, now);
  const timeText = formatDisplayTime(now);

  return (
    <main className="screen-shell" ref={ref}>
      <section className="display-canvas" style={{ transform: `translate(-50%, -50%) scale(${scale})` }}>
        <header className="display-header">
          <div className="system-time">
            <Clock size={28} />
            {timeText}
          </div>
          <div className="title-frame">
            <span>{snapshot.title}</span>
          </div>
        </header>

        <div className="dashboard-grid">
          <div className="column left-column">
            <Panel title="营业执照" className="license-panel">
              <div className="license-grid">
                {snapshot.licenseCards.map((card) => (
                  <article className="license-card" key={card.id}>
                    <img src={card.imageUrl} alt={card.title} />
                    <strong>{card.title}</strong>
                    <span>{card.subtitle}</span>
                  </article>
                ))}
              </div>
            </Panel>

            <Panel title="留样公示" className="retention-panel">
              <AutoScrollTable
                className="retention-table"
                columns={RETENTION_COLUMNS}
                getRowKey={(row) => row.id}
                rows={snapshot.sampleRetentionRows}
                visibleRows={RETENTION_VISIBLE_ROWS}
                renderCells={(row) => (
                  <>
                      <td>{row.id}</td>
                      <td>{row.dish}</td>
                      <td>{row.weight}</td>
                      <td>{row.meal}</td>
                      <td>{row.keeper}</td>
                  </>
                )}
              />
            </Panel>
          </div>

          <div className="column center-column">
            <Panel title="实时到店人数" className="attendance-panel">
              <div className="attendance-label">当前人数</div>
              <div className="attendance-number">{attendanceCount}</div>
            </Panel>

            <Panel title="后厨实时监控" className="camera-panel">
              <div className="camera-grid">
                {snapshot.cameras.slice(0, 4).map((camera) => (
                  <article className="camera-tile" key={camera.id} data-camera-slot={camera.slot}>
                    <div className="camera-placeholder">
                      <Camera size={54} />
                      <span>摄像头离线占位</span>
                    </div>
                    <div className="camera-name">{camera.name}</div>
                  </article>
                ))}
              </div>
            </Panel>
          </div>

          <div className="column right-column">
            <Panel title="健康证更新" className="health-panel">
              <div className="health-top">
                <div className="donut" style={{ "--donut": buildDonut(healthCertificates.counts) } as CSSProperties}>
                  <div>
                    <strong>{healthCertificates.total}</strong>
                    <span>张</span>
                  </div>
                </div>
                <div className="health-legend">
                  {(Object.keys(HEALTH_LABELS) as HealthStatus[]).map((status) => (
                    <div key={status} className={HEALTH_CLASS[status]}>
                      <i />
                      <span>{HEALTH_LABELS[status]}</span>
                      <strong>{healthCertificates.counts[status]}张</strong>
                    </div>
                  ))}
                </div>
              </div>
              <AutoScrollTable
                className="health-table"
                columns={HEALTH_COLUMNS}
                getRowKey={(row) => row.id}
                rows={healthCertificates.rows}
                visibleRows={HEALTH_VISIBLE_ROWS}
                renderCells={(row) => (
                  <>
                      <td className="health-name">
                        <img src={row.avatarUrl} alt="" />
                        {row.name}
                      </td>
                      <td>{row.idNumberMasked}</td>
                      <td>
                        <span className={`pill ${HEALTH_CLASS[row.status]}`}>{HEALTH_LABELS[row.status]}</span>
                      </td>
                      <td>{row.validUntil}</td>
                  </>
                )}
              />
            </Panel>

            <Panel title="质检报告更新" className="report-panel">
              <div className="report-grid">
                {reports.map((report) => (
                  <ReportCard key={report.category} report={report} />
                ))}
              </div>
            </Panel>
          </div>
        </div>
      </section>
    </main>
  );
}

interface AutoScrollTableProps<T> {
  className: string;
  columns: string[];
  getRowKey: (row: T) => string | number;
  renderCells: (row: T) => ReactNode;
  rows: T[];
  visibleRows: number;
}

function AutoScrollTable<T>({ className, columns, getRowKey, renderCells, rows, visibleRows }: AutoScrollTableProps<T>) {
  const shouldScroll = rows.length > visibleRows;
  const scrollDuration = Math.max(18, rows.length * 3.6);
  const style = {
    "--scroll-duration": `${scrollDuration}s`
  } as CSSProperties;

  return (
    <div className={`scroll-table ${className}`} data-scroll={String(shouldScroll)} style={style}>
      <table className="data-table scroll-table-head">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
      </table>
      <div className="scroll-table-body">
        <div className="scroll-table-track">
          <table className="data-table scroll-table-copy">
            <tbody>{renderAutoScrollRows(rows, getRowKey, renderCells)}</tbody>
          </table>
          {shouldScroll && (
            <table className="data-table scroll-table-copy" aria-hidden="true">
              <tbody>{renderAutoScrollRows(rows, getRowKey, renderCells)}</tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function renderAutoScrollRows<T>(rows: T[], getRowKey: (row: T) => string | number, renderCells: (row: T) => ReactNode) {
  return rows.map((row) => <tr key={getRowKey(row)}>{renderCells(row)}</tr>);
}

function Panel({ title, className, children }: { title: string; className?: string; children: ReactNode }) {
  return (
    <section className={`dashboard-panel ${className ?? ""}`}>
      <h2>{title}</h2>
      <div className="panel-body">{children}</div>
    </section>
  );
}

function ReportCard({ report }: { report: QualityReportStatus }) {
  const title = report.title ?? "检测报告待上传";

  return (
    <article className="report-card">
      <div className="report-title">{report.label}</div>
      <div className={`report-paper ${report.assetUrl ? "has-image" : ""}`}>
        {report.assetUrl ? (
          <div className="report-paper-image-frame">
            <img className="report-paper-image" src={report.assetUrl} alt={`${report.label}检测报告图片`} />
          </div>
        ) : (
          <FileCheck2 size={46} />
        )}
        {!report.assetUrl && (
          <div className="report-paper-caption">
            <span>{title}</span>
            <small>今日暂无报告</small>
          </div>
        )}
      </div>
      <div className={`report-badge ${report.updatedToday ? "updated" : "missing"}`}>
        {report.updatedToday ? <CircleCheck size={26} /> : <CircleAlert size={26} />}
        {report.updatedToday ? "已更新" : "未更新"}
      </div>
    </article>
  );
}

function buildDonut(counts: Record<HealthStatus, number>): string {
  const total = Object.values(counts).reduce((sum, value) => sum + value, 0) || 1;
  const normal = (counts.normal / total) * 360;
  const expiring = normal + (counts.expiring / total) * 360;
  const expired = expiring + (counts.expired / total) * 360;

  return `#54f1a6 0deg ${normal}deg, #46b8ff ${normal}deg ${expiring}deg, #ff8a4c ${expiring}deg ${expired}deg, #f5d65b ${expired}deg 360deg`;
}

function formatDisplayTime(date: Date): string {
  const weekDays = ["星期日", "星期一", "星期二", "星期三", "星期四", "星期五", "星期六"];
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day} ${weekDays[date.getDay()]} ${hour}:${minute}`;
}
